import {parseInteger} from '@benchling/js-utils';
import _ from 'underscore';

import {assert} from 'Asserts';
import {decodeJQueryParam, setURLParams} from 'BenchlingUtil';
import {StringIDFolder} from 'constants/FolderConstants';
import {getSelectedPageSize} from 'nav/sidepanel/PageSizeUtil';
import ReviewConstants from 'notebook/reviews/ReviewConstants';
import notifications from 'notifications';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {isMultiQueryBLAST} from 'search/blast/BLASTUtil';
import * as FilterUtil from 'search/FilterUtil';
import {
  BLASTCompAdjustment,
  BLASTFilter,
  BLASTMatrix,
  BLASTNParams,
  BLASTNTask,
  BLASTParamsUnion,
  BLASTPParams,
  BLASTStrand,
  BLASTTextSequence,
  ChemicalStructureFilterFormat,
  DEPRECATED_FILTER_PREFIXES,
  FieldFilter,
  Filter,
  FILTER_KEY_TO_OPERATORS,
  FILTER_PART_SEPARATOR,
  FILTER_SEPARATOR,
  FILTER_VALUE_SEPARATOR,
  FilterErrors,
  FilterKey,
  FILTERS,
  GroupByKey,
  isBLASTFilter,
  isChemicalStructureFilter,
  isCustomTagFilter,
  isDatetimeFilter,
  isDatetimeFilterKey,
  isFieldFilter,
  isFullTextFilterKey,
  isMonomerFilter,
  isMultiValueFieldType,
  isMultiValueFilter,
  isMultiValueFilterKey,
  isSingleValueFilter,
  isSingleValueFilterKey,
  isStandardFilter,
  isStandardFilterArrayValue,
  isStandardFilterKey,
  isStandardFilterRangeValue,
  isStandardFilterRelativeRangeValue,
  isStandardFilterScalarValue,
  isTextFieldType,
  MentionableType,
  MonomerFilterQualifier,
  NULLABLE_OPERATORS,
  Operator,
  PrimitiveFilterValue,
  RelativeRangeUnits,
  ReviewFilter,
  SearchParams,
  Sort,
  SORT_PART_SEPARATOR,
  SORT_REVERSE_FALSE_URL_STRING,
  SORT_REVERSE_TRUE_URL_STRING,
  SORT_SEPARATOR,
  StandardFilter,
  StandardFilterDataType,
  StandardFilterKey,
  StandardFilterValue,
  standardOperatorsForKey,
  StandardRangeOperatorValue,
  StandardRelativeRangeOperatorValue,
  VALUELESS_OPERATORS,
} from 'search/SearchConstants';
import {sanitizeEmptyFilters, sanitizeSearchParams} from 'search/SearchUtil';
import {NavigationUpdate} from 'search/withSearching';
import {compressToURLSafeBase64, decompressFromURLSafeBase64} from 'util/CompressionUtil';

/*
Used to preserve backwards compatibility in param parsing. When making a backwards incompatible change the
URL_VERSION constant should be incremented manually, an entry added to this comment explaining the change
in reverse chronological order (most recent at the top), and the parsing logic should be updated so previous
versions will continue to work.

v1: Added URL encoding to "sorts" param values
v2: Started encoding operator for SEQUENCE_BASES, OLIGO_HELM_DNA, and OLIGO_HELM_RNA filters
*/
export const URL_VERSION = 2;

const PRE_VERSIONING_DEFAULT = 0;
const NULL_STRING = 'null';

type StandardOperatorScalarParser = (value: string) => PrimitiveFilterValue;

function parseNumber(value: string): number | null {
  return value ? Number(FilterUtil.decodeValue(value)) : null;
}

function parseString(value: string): string {
  return FilterUtil.decodeValue(value);
}

function parseApiId(value: string): string | null {
  return FilterUtil.decodeValue(value) || null;
}

function parseDatetime(value: string): string | null {
  if (value === 'null') {
    return null;
  }
  return FilterUtil.decodeValue(value) || null;
}

function parseBool(value: string): string | null {
  return FilterUtil.decodeValue(value) || null;
}

function parseStandardFilterScalarValue(
  value: string,
  scalarParser: StandardOperatorScalarParser
): PrimitiveFilterValue {
  return scalarParser(value);
}

function parseStandardFilterArrayValue(
  value: string,
  scalarParser: StandardOperatorScalarParser
): Array<PrimitiveFilterValue> {
  return value.split(FILTER_VALUE_SEPARATOR).map((item) => scalarParser(item));
}

function parseStandardFilterRangeValue(
  value: string,
  scalarParser: StandardOperatorScalarParser
): StandardRangeOperatorValue {
  const rawValues = value.split(FILTER_VALUE_SEPARATOR);
  if (rawValues.length !== 2) {
    throw new Error(`Range filter value must have exactly two elements`);
  }
  return {
    from: scalarParser(rawValues[0]),
    to: scalarParser(rawValues[1]),
  };
}

function parseStandardFilterRelativeRangeValue(
  value: string,
  scalarParser: StandardOperatorScalarParser
): StandardRelativeRangeOperatorValue {
  const rawValues = value.split(FILTER_VALUE_SEPARATOR);
  if (rawValues.length !== 4) {
    throw new Error(`Relative range filter value must have exactly four elements`);
  }
  return {
    fromUnits: rawValues[0] ? (rawValues[0] as RelativeRangeUnits) : null,
    from: parseNumber(rawValues[1]),
    toUnits: rawValues[2] ? (rawValues[2] as RelativeRangeUnits) : null,
    to: parseNumber(rawValues[3]),
  };
}

function parseStandardFilterValuelessValue(
  value: string,
  scalarParser: StandardOperatorScalarParser
): StandardFilterValue {
  throw new Error('Attempted to parse a value for a valueless operator');
}

const STANDARD_OPERATOR_SCALAR_PARSER: {[key in StandardFilterDataType]: StandardOperatorScalarParser} = {
  [StandardFilterDataType.INT]: parseNumber,
  [StandardFilterDataType.FLOAT]: parseNumber,
  [StandardFilterDataType.DATE]: parseDatetime,
  [StandardFilterDataType.DATETIME]: parseDatetime,
  [StandardFilterDataType.STRING]: parseString,
  [StandardFilterDataType.BOOL]: parseBool,
  [StandardFilterDataType.ENUM]: parseString,
  [StandardFilterDataType.SINGLE_VALUE_ENTITY_LINK]: parseApiId,
  [StandardFilterDataType.MULTI_VALUE_ENTITY_LINK]: parseApiId,
  [StandardFilterDataType.VARIABLE]: parseString,
};

const STANDARD_FILTER_OPERATOR_PARSER: {
  [key in Operator]?: (value: string, scalarParser: StandardOperatorScalarParser) => StandardFilterValue;
} = {
  [Operator.EQ]: parseStandardFilterScalarValue,
  [Operator.IS]: parseStandardFilterScalarValue,
  [Operator.IS_ONE_OF]: parseStandardFilterArrayValue,
  [Operator.IS_NOT]: parseStandardFilterScalarValue,
  [Operator.IS_NONE_OF]: parseStandardFilterArrayValue,
  [Operator.GTE]: parseStandardFilterScalarValue,
  [Operator.LTE]: parseStandardFilterScalarValue,
  [Operator.BETWEEN]: parseStandardFilterRangeValue,
  [Operator.IS_EMPTY]: parseStandardFilterValuelessValue,
  [Operator.IS_NOT_EMPTY]: parseStandardFilterValuelessValue,
  [Operator.IS_TRUE]: parseStandardFilterValuelessValue,
  [Operator.IS_FALSE]: parseStandardFilterValuelessValue,
  [Operator.CURRENT_USER]: parseStandardFilterValuelessValue,
  [Operator.INCLUDES_TEXT]: parseStandardFilterArrayValue,
  [Operator.EXCLUDES_TEXT]: parseStandardFilterArrayValue,
  [Operator.STARTSWITH_TEXT]: parseStandardFilterArrayValue,
  [Operator.HAS_ONE_OF]: parseStandardFilterArrayValue,
  [Operator.HAS_NONE_OF]: parseStandardFilterArrayValue,
  [Operator.HAS_ALL_OF]: parseStandardFilterArrayValue,
  [Operator.HAS_ONLY]: parseStandardFilterArrayValue,
  [Operator.RELATIVE_RANGE]: parseStandardFilterRelativeRangeValue,
};

export function parseStandardOperatorValue(
  operator: Operator,
  fieldDataType: StandardFilterDataType,
  rawFilterString: string
): StandardFilterValue {
  if (!STANDARD_FILTER_OPERATOR_PARSER.hasOwnProperty(operator)) {
    throw new Error(`${operator} not found in STANDARD_FILTER_OPERATOR_PARSER`);
  }

  const scalarParser = STANDARD_OPERATOR_SCALAR_PARSER[fieldDataType];
  return STANDARD_FILTER_OPERATOR_PARSER[operator]!(rawFilterString, scalarParser);
}

export function parseStandardFilterString(key: StandardFilterKey, rawFilterString: string): StandardFilter {
  const filterSeparator = rawFilterString.indexOf(FILTER_PART_SEPARATOR);
  const filterConfig = FILTERS[key];
  const fieldDataType = filterConfig.dataType;

  if (!fieldDataType) {
    throw new Error(`Standard filter ${key} does not have a configured data type.`);
  }

  const operator = FilterUtil.decodeValue(
    filterSeparator === -1 ? rawFilterString : rawFilterString.substring(0, filterSeparator)
  ) as Operator;

  const valueString = operator ? rawFilterString.substring(operator.length + 1) : '';

  const allowedOperators = [
    ...standardOperatorsForKey(key),
    ...(filterConfig.isNullable ? NULLABLE_OPERATORS : []),
  ];

  // Nullable multi-part filter strings had an additional part for the null value. They also may or may not
  // have had an operator, so check this before the normal operator checka we do for other backwards compat
  // checks.
  if (
    [
      FilterKey.PROCESS_RESPONSIBLE_TEAM,
      FilterKey.PROCESS_TASK_ASSIGNEE,
      FilterKey.PROCESS_WATCHER,
      FilterKey.ROOT_PROCESS_WATCHER,
    ].includes(key)
  ) {
    const rawParts = rawFilterString.split(FILTER_PART_SEPARATOR);
    let hasLegacyNull = false;
    let newValueString: string;
    let newOperator = operator;
    if (!allowedOperators.includes(operator) && rawParts.length === 2) {
      if (fieldDataType === StandardFilterDataType.MULTI_VALUE_ENTITY_LINK) {
        newOperator = Operator.HAS_ONE_OF;
      } else {
        newOperator = Operator.IS_ONE_OF;
      }
      newValueString = rawParts[1];
      if (rawParts[0] === 'true') {
        newValueString += FILTER_VALUE_SEPARATOR;
      }
      hasLegacyNull = true;
    } else if (rawParts.length === 3) {
      newValueString = rawParts[2];
      if (rawParts[1] === 'true') {
        // This skips the case when only the None is selected
        if (newValueString !== '') {
          newValueString += FILTER_VALUE_SEPARATOR;
        }
      }
      hasLegacyNull = true;
    }

    if (hasLegacyNull) {
      return parseStandardFilterString(key, `${newOperator}${FILTER_PART_SEPARATOR}${newValueString!}`);
    }
  }
  if (!allowedOperators.includes(operator)) {
    // supporting backcompat for old datetime filters as the old datetime filters didn't have an operator
    if (
      key === FilterKey.MODIFIED_AT ||
      key === FilterKey.CREATED_AT ||
      key === FilterKey.PROCESS_TASK_SCHEDULED_ON_DATE
    ) {
      // PROCESS_TASK_SCHEDULED_ON_DATE supported the IS_EMPTY operator even before standard filters, but
      // encoded it in the URL as a range with both values as null without an operator. The old URL parsing
      // logic actually had a bug where it would error if you tried to parse a URL for IS_EMPTY, but we
      // can actually detect that and set the operator as expected.
      if (rawFilterString === `null${FILTER_VALUE_SEPARATOR}null`) {
        return {key, operator: Operator.IS_EMPTY, value: null};
      }
      return parseStandardFilterString(key, `${Operator.BETWEEN}${FILTER_PART_SEPARATOR}${rawFilterString}`);
    } else if (key === FilterKey.REVIEW_LIFECYCLE) {
      if (rawFilterString === 'null') {
        return {key, operator: Operator.IS_NOT_EMPTY, value: null};
      } else {
        return parseStandardFilterString(
          key,
          `${Operator.IS_ONE_OF}${FILTER_PART_SEPARATOR}${rawFilterString}`
        );
      }
    } else if (
      fieldDataType === StandardFilterDataType.SINGLE_VALUE_ENTITY_LINK &&
      [Operator.IS, Operator.IS_NOT].includes(operator)
    ) {
      // IS and IS_NOT operators were originally included on the client for SINGLE_VALUE_ENTITY_LINK
      // fields, but we really don't want to display them since they are redundant with IS_ONE_OF and
      // IS_NONE_OF. However, we need to support parsing them from the URL for backwards compatibility since
      // we released a filter with this data type before removing them, so people may have saved URLs -
      // especially since IS was the default.
      // Note that this will cause an IS or IS_NOT filter with no FILTER_PART_SEPARATOR to be parsed as if
      // it did have a value with no elements, but that's an okay tradeoff since it's still a valid state
      // that will only come up in a rare edge case, and handling it otherwise would require restructuring
      // this function to handle some conversions before value validation and others after.
      const newOperator = operator === Operator.IS ? Operator.IS_ONE_OF : Operator.IS_NONE_OF;
      return parseStandardFilterString(key, `${newOperator}${FILTER_PART_SEPARATOR}${valueString}`);
    } else if (
      // Some filters that were set as multi value filters in the past defaulted to using only the IS_ONE_OF operator,
      // but if it's a multi value entity link filter, then HAS_ONE_OF should be the corresponding operator to use.
      // This is the case for RequestAssigneeFilter, and maybe some more.
      fieldDataType === StandardFilterDataType.MULTI_VALUE_ENTITY_LINK &&
      operator === Operator.IS_ONE_OF
    ) {
      return parseStandardFilterString(key, `${Operator.HAS_ONE_OF}${FILTER_PART_SEPARATOR}${valueString}`);
    }
    throw new Error(`Standard filter ${key} has unsupported operator ${operator} for its data type.`);
  }

  if (VALUELESS_OPERATORS.includes(operator)) {
    return {
      key,
      operator,
      value: null,
    };
  }

  // Check filterSeparator instead of valueString because an empty string might be a legitimate value.
  // We only know if a value was missing ie separator is missing.
  if (filterSeparator === -1) {
    throw new Error(`Standard filter ${key} does not have a value for operator ${operator}.`);
  }

  // supporting backcompat for owner filter since the shape of the value has changed
  if (key === FilterKey.OWNER && valueString.includes(FILTER_PART_SEPARATOR)) {
    const [userIds, organizationIds] = valueString
      .split(FILTER_PART_SEPARATOR)
      .map((val) => FilterUtil.decodeValue(val));
    return {
      key,
      operator,
      value: [
        ...(userIds ? userIds.split(FILTER_VALUE_SEPARATOR) : []),
        ...(organizationIds ? organizationIds.split(FILTER_VALUE_SEPARATOR) : []),
      ],
    };
  }
  if (key === FilterKey.OWNER_OR_COLLABORATOR && valueString.includes(FILTER_PART_SEPARATOR)) {
    const [userIds, organizationIds, teamIds] = valueString
      .split(FILTER_PART_SEPARATOR)
      .map((val) => FilterUtil.decodeValue(val));
    return {
      key,
      operator,
      value: [
        ...(userIds ? userIds.split(FILTER_VALUE_SEPARATOR) : []),
        ...(organizationIds ? organizationIds.split(FILTER_VALUE_SEPARATOR) : []),
        ...(teamIds ? teamIds.split(FILTER_VALUE_SEPARATOR) : []),
      ],
    };
  }

  const value = parseStandardOperatorValue(operator, fieldDataType, valueString);
  return {key, operator, value};
}

export function parseFiltersString(filtersString: string, urlVersion: number): Array<Filter> {
  if (!filtersString) {
    return [];
  }
  const filters = filtersString
    .split(FILTER_SEPARATOR)
    .reduce((filters: Array<Filter>, filterString: string): Array<Filter> => {
      const filterSeparator = filterString.indexOf(FILTER_PART_SEPARATOR);
      const filterPrefix = filterString.substring(0, filterSeparator + 1);
      if (DEPRECATED_FILTER_PREFIXES.has(filterPrefix)) {
        return filters;
      }
      const rawValue = filterString.substring(filterPrefix.length);
      const filter = _.findWhere(Object.values(FILTERS), {prefix: filterPrefix});
      if (!filter) {
        throw new Error(`Unexpected filter prefix '${filterPrefix}'`);
      }
      const key = filter.key;
      if (
        key === FilterKey.PROTEIN_AMINO_ACIDS &&
        rawValue.indexOf(FILTER_PART_SEPARATOR) === -1 &&
        rawValue !== Operator.IS_NOT_EMPTY
      ) {
        // This is a stale search, from before PROTEIN_AMINO_ACIDS was converted to a FullTextFilterKey.
        filters.push({
          key,
          operator: Operator.INCLUDES_TEXT,
          value: rawValue,
        });
        return filters;
      }
      if (isFullTextFilterKey(key)) {
        if (urlVersion < 2) {
          notifications.warn(
            `${FILTERS[key].name} filter was omitted from your search query due to an error. Please try adding it again.`
          );
          // Setting an empty value here so that this will be removed by sanitizeEmptyFilters
          filters.push({key, operator: Operator.IS, value: ''});
          return filters;
        }
        const [operator, value] = rawValue.split(FILTER_PART_SEPARATOR);
        filters.push({key, operator: operator as Operator, value});
        return filters;
      }
      if (isSingleValueFilterKey(key)) {
        filters.push({
          key,
          operator: Operator.IS,
          value: rawValue === NULL_STRING ? null : rawValue,
        });
        return filters;
      }
      if (isMultiValueFilterKey(key)) {
        const rawValueParts = rawValue.split(FILTER_PART_SEPARATOR);
        let operator;
        let convertedValue;
        if (rawValueParts.length === 1) {
          operator = FILTER_KEY_TO_OPERATORS[key][0];
          convertedValue = rawValueParts[0];
        } else {
          [operator, convertedValue] = rawValueParts;
        }
        if (!Object.keys(Operator).includes(operator)) {
          throw new Error(`Unexpected operator ${operator}`);
        }
        let updatedFilter = {
          key,
          operator: operator as Operator,
          value: convertedValue ? convertedValue.split(FILTER_VALUE_SEPARATOR) : [],
        };

        if (key === FilterKey.TYPES) {
          updatedFilter = FilterUtil.replaceProcedureTypeFilter(updatedFilter);
        }
        filters.push(updatedFilter);
        return filters;
      }
      if (isDatetimeFilterKey(key)) {
        const [fromDatetime, toDatetime] = rawValue.split(FILTER_VALUE_SEPARATOR);
        filters.push({
          key,
          operator: Operator.IS,
          value: {fromDatetime, toDatetime},
        });
        return filters;
      }
      if (key === FilterKey.CUSTOM_TAG) {
        const filterParts = rawValue.split(FILTER_PART_SEPARATOR).map((val) => FilterUtil.decodeValue(val));
        let name;
        let value;
        let operator;
        if (filterParts.length === 2) {
          [name, value] = filterParts;
          operator = Operator.IS;
        } else {
          [operator, name, value] = filterParts;
        }
        filters.push({
          key,
          operator: operator as Operator,
          name,
          value,
        });
        return filters;
      }
      if (key === FilterKey.SCHEMA_FIELD || key === FilterKey.BATCH_TAG) {
        filters.push(parseFieldFilterString(key, rawValue));
        return filters;
      }
      if (key === FilterKey.REVIEW) {
        filters.push(parseReviewFieldFilterString(rawValue));
        return filters;
      }
      if (key === FilterKey.ALPHAFOLD_STRUCTURE) {
        filters.push({key, operator: rawValue as Operator, value: null});
        return filters;
      }
      if (key === FilterKey.CONTAINER_QUANTITY || key === FilterKey.CONTAINER_CONCENTRATION) {
        const [operator, value, units] = rawValue.split(FILTER_PART_SEPARATOR);
        filters.push({key, operator: operator as Operator, value: {value: Number(value) || 0, units}});
        return filters;
      }
      if (key === FilterKey.GRID_EMPTY_POSITIONS) {
        const [operator, value] = rawValue.split(FILTER_PART_SEPARATOR);
        filters.push({key, operator: operator as Operator, value});
        return filters;
      }
      if (key === FilterKey.CONTAINER_CONTENTS) {
        const [operator, convertedValue] = rawValue.split(FILTER_PART_SEPARATOR);
        if (!Object.keys(Operator).includes(operator)) {
          throw new Error(`Unexpected operator ${operator}`);
        }
        filters.push({
          key,
          operator: operator as Operator,
          value: {
            contentIds: convertedValue ? convertedValue.split(FILTER_VALUE_SEPARATOR) : [],
          },
        });
        return filters;
      } else if (key === FilterKey.NOTE_REFERENCE) {
        const [operator, convertedValue, referencedType] = rawValue.split(FILTER_PART_SEPARATOR);
        if (!Object.keys(Operator).includes(operator)) {
          throw new Error(`Unexpected operator ${operator}`);
        }
        filters.push({
          key,
          operator: operator as Operator,
          value: {
            referencedType: referencedType as MentionableType,
            referencedIds: convertedValue ? convertedValue.split(FILTER_VALUE_SEPARATOR) : [],
          },
        });
        return filters;
      }
      if (key === FilterKey.MONOMER) {
        const [operator, value, qualifier, rawQualifierValue] = rawValue.split(FILTER_PART_SEPARATOR);
        const qualifierValue =
          qualifier === MonomerFilterQualifier.AT_POS
            ? rawQualifierValue.split(FILTER_VALUE_SEPARATOR).map((v) => parseInteger(v))
            : qualifier === MonomerFilterQualifier.ANY_POS
              ? undefined
              : parseInteger(rawQualifierValue);
        filters.push({
          key,
          operator: operator as Operator,
          value,
          qualifier: qualifier as MonomerFilterQualifier,
          qualifierValue: qualifierValue as Array<number> | number | undefined,
        });
        return filters;
      }
      if (key === FilterKey.CHEMICAL_STRUCTURE) {
        const values = rawValue.split(FILTER_PART_SEPARATOR);
        const [operator, value, format, ...options] = values;
        if (operator === Operator.SUBSTRUCTURE) {
          const [reactionComponentRole] = options;
          if (options.length > 1) {
            throw new Error(`Unexpected chemical structure filter options`);
          }
          filters.push({
            key,
            operator: operator as Operator,
            value: decompressFromURLSafeBase64(value),
            format: format as ChemicalStructureFilterFormat,
            ...(reactionComponentRole != null
              ? {reactionComponentRole: FilterUtil.decodeValue(reactionComponentRole)}
              : {}),
          });
        } else if (operator === Operator.SIMILARITY) {
          const [minSimilarity, reactionComponentRole] = options;
          if (minSimilarity == null || options.length > 2) {
            throw new Error(`Unexpected chemical structure filter options`);
          }
          filters.push({
            key,
            operator: operator as Operator,
            value: decompressFromURLSafeBase64(value),
            format: format as ChemicalStructureFilterFormat,
            minSimilarity: parseFloat(minSimilarity),
            ...(reactionComponentRole != null
              ? {reactionComponentRole: FilterUtil.decodeValue(reactionComponentRole)}
              : {}),
          });
        } else {
          throw new Error(`Unexpected operator ${operator}`);
        }
        return filters;
      }
      if (key === FilterKey.BLAST) {
        const rawValueParts = rawValue.split(FILTER_PART_SEPARATOR);
        if (rawValueParts.length < 3 || rawValueParts.length > 4) {
          throw new Error('Unexpected parts for BLAST filter');
        }
        const [operator, convertedTextSequences, convertedApiIdentifiers, convertedParams] = rawValueParts;
        if (!Object.keys(Operator).includes(operator)) {
          throw new Error(`Unexpected operator ${operator}`);
        }
        filters.push({
          key,
          operator: operator as Operator,
          value: {
            textSequences: parseBLASTTextSequences(decompressFromURLSafeBase64(convertedTextSequences)),
            apiIdentifiers: convertedApiIdentifiers
              ? convertedApiIdentifiers.split(FILTER_VALUE_SEPARATOR)
              : [],
          },
          params: parseBLASTParams(convertedParams),
        });
        return filters;
      }
      // @ts-expect-error TODO(BNCH-56923): This branch is never hit since the check to
      // `isMultiValueFilterKey` short-circuits.
      // A few possible solutions:
      // - Remove this branch
      // - Move this branch above `isMultiValueFilterKey`
      // - Remove `FilterKey.PART_SEQUENCE` from `MULTI_VALUE_FILTER_KEYS`
      if (key === FilterKey.PART_SEQUENCE) {
        const [operator, value] = rawValue.split(FILTER_PART_SEPARATOR);
        filters.push({
          key,
          operator: operator as Operator,
          value: decompressFromURLSafeBase64(value),
        });
        return filters;
      }
      if (isStandardFilterKey(key)) {
        filters.push(parseStandardFilterString(key, rawValue));
        return filters;
      }
      throw new Error(`Unsupported filter key ${key}`);
    }, []);
  return filters;
}

function parseFieldFilterString(
  key: FilterKey.BATCH_TAG | FilterKey.SCHEMA_FIELD,
  filterString: string
): FieldFilter {
  const filterParts = filterString.split(FILTER_PART_SEPARATOR).map((val) => FilterUtil.decodeValue(val));
  let operator;
  let rawValue;
  let fieldId;
  let fieldType;
  if (filterParts.length === 3) {
    // For valueless operator, filterParts will only have 3 parts
    [operator, fieldId, fieldType] = filterParts as [Operator, string, FieldType];
    rawValue = null;
  } else {
    [operator, rawValue, fieldId, fieldType] = filterParts as [Operator, string, string, FieldType];
  }
  let value;
  if (VALUELESS_OPERATORS.includes(operator)) {
    value = null;
  } else {
    let standardFieldType;
    if (isMultiValueFieldType(fieldType)) {
      standardFieldType = StandardFilterDataType.MULTI_VALUE_ENTITY_LINK;
    } else if (isTextFieldType(fieldType)) {
      standardFieldType = StandardFilterDataType.STRING;
    } else if (fieldType === FieldType.BOOLEAN) {
      standardFieldType = StandardFilterDataType.BOOL;
    } else if (fieldType === FieldType.DATE) {
      standardFieldType = StandardFilterDataType.DATE;
    } else if (fieldType === FieldType.DATETIME) {
      standardFieldType = StandardFilterDataType.DATETIME;
    } else if (fieldType === FieldType.INTEGER) {
      standardFieldType = StandardFilterDataType.INT;
    } else if (fieldType === FieldType.FLOAT) {
      standardFieldType = StandardFilterDataType.FLOAT;
    } else {
      throw new Error(`Unsupported fieldType ${fieldType}`);
    }

    if (rawValue == null) {
      throw new Error(`FieldType ${fieldType} can't have null value.`);
    }

    value = parseStandardOperatorValue(operator, standardFieldType, rawValue);
  }
  return {key, operator, value, fieldId, fieldType} as FieldFilter;
}

function parseReviewFieldFilterString(filterString: string): ReviewFilter {
  const reviewStatusFields = filterString.split(ReviewConstants.ROLE_FILTER_SEPARATOR);
  const rawReviewStatus = reviewStatusFields[0];
  const rawRole = reviewStatusFields[1];
  return {
    key: FilterKey.REVIEW,
    operator: Operator.IS,
    value: {
      review_status: rawReviewStatus === 'null' ? null : rawReviewStatus,
      role: rawRole === 'null' ? null : rawRole,
    },
  };
}

function parseBLASTTextSequences(textSequencesString: string): Array<BLASTTextSequence> {
  const textSequences: Array<BLASTTextSequence> = [];
  // Make sure we don't try to parse an empty string, which signifies no text sequences.
  if (textSequencesString) {
    const textSequencesElements = textSequencesString.split(FILTER_VALUE_SEPARATOR);
    if (textSequencesElements.length % 2 !== 0) {
      throw new Error('BLAST filter textSequences does not have name/sequence pairs.');
    }
    for (let i = 0; i < textSequencesElements.length; i += 2) {
      textSequences.push({
        name: textSequencesElements[i],
        sequence: textSequencesElements[i + 1],
      });
    }
  }
  return textSequences;
}

function parseBLASTParams(paramsString: string): BLASTParamsUnion | undefined {
  if (!paramsString) {
    return undefined;
  }
  const params: BLASTParamsUnion = {};
  const paramsElements = paramsString.split(FILTER_VALUE_SEPARATOR);
  if (paramsElements.length % 2 !== 0) {
    throw new Error('BLAST filter params does not have key/value pairs.');
  }
  for (let i = 0; i < paramsElements.length; i += 2) {
    switch (paramsElements[i]) {
      case 'task':
        (params as BLASTNParams).task = paramsElements[i + 1] as BLASTNTask;
        break;
      case 'evalue':
        (params as BLASTNParams).evalue = Number(paramsElements[i + 1]);
        break;
      case 'word_size':
        (params as BLASTNParams).word_size = Number(paramsElements[i + 1]);
        break;
      case 'culling_limit':
        (params as BLASTNParams).culling_limit = Number(paramsElements[i + 1]);
        break;
      case 'gapopen':
        (params as BLASTNParams).gapopen = Number(paramsElements[i + 1]);
        break;
      case 'gapextend':
        (params as BLASTNParams).gapextend = Number(paramsElements[i + 1]);
        break;
      case 'reward':
        (params as BLASTNParams).reward = Number(paramsElements[i + 1]);
        break;
      case 'penalty':
        (params as BLASTNParams).penalty = Number(paramsElements[i + 1]);
        break;
      case 'strand':
        (params as BLASTNParams).strand = paramsElements[i + 1] as BLASTStrand;
        break;
      case 'matrix':
        (params as BLASTPParams).matrix = paramsElements[i + 1] as BLASTMatrix;
        break;
      case 'comp_based_stats':
        (params as BLASTPParams).comp_based_stats = paramsElements[i + 1] as BLASTCompAdjustment;
        break;
      default:
        throw new Error(`Unexpected BLAST param ${paramsElements[i]}`);
    }
  }
  return params;
}

function stringifyStandardFilterScalarValue(value: StandardFilterValue): string {
  if (value != null && !isStandardFilterScalarValue(value)) {
    throw new Error(`Attempted to call stringifyStandardFilterScalarValue with non-scalar value ${value}`);
  }
  return FilterUtil.encodeValue(value ?? '');
}

function stringifyStandardFilterArrayValue(value: StandardFilterValue): string {
  if (!isStandardFilterArrayValue(value)) {
    throw new Error(`Attempted to call stringifyStandardFilterArrayValue with non-array value ${value}`);
  }
  return value.map((item) => FilterUtil.encodeValue(item ?? '')).join(FILTER_VALUE_SEPARATOR);
}

function stringifyStandardFilterRangeValue(value: StandardFilterValue): string {
  if (!isStandardFilterRangeValue(value)) {
    throw new Error(`Attempted to call stringifyStandardFilterRangeValue with non-range value ${value}`);
  }
  return (
    FilterUtil.encodeValue(value.from ?? '') + FILTER_VALUE_SEPARATOR + FilterUtil.encodeValue(value.to ?? '')
  );
}

function stringifyStandardFilterRelativeRangeValue(value: StandardFilterValue): string {
  if (!isStandardFilterRelativeRangeValue(value)) {
    throw new Error(
      `Attempted to call stringifyStandardFilterRelativeRangeValue with non-relative-range value ${value}`
    );
  }
  return (
    FilterUtil.encodeValue(value.fromUnits ?? '') +
    FILTER_VALUE_SEPARATOR +
    FilterUtil.encodeValue(value.from ?? '') +
    FILTER_VALUE_SEPARATOR +
    FilterUtil.encodeValue(value.toUnits ?? '') +
    FILTER_VALUE_SEPARATOR +
    FilterUtil.encodeValue(value.to ?? '')
  );
}

function stringifyStandardFilterValuelessValue(value: StandardFilterValue): string {
  throw new Error('Attempted to stringify a value for a valueless operator');
}

const STANDARD_FILTER_OPERATOR_STRINGIFIER: {
  [key in Operator]?: (value: StandardFilterValue | null) => string;
} = {
  [Operator.EQ]: stringifyStandardFilterScalarValue,
  [Operator.IS]: stringifyStandardFilterScalarValue,
  [Operator.IS_ONE_OF]: stringifyStandardFilterArrayValue,
  [Operator.IS_NOT]: stringifyStandardFilterScalarValue,
  [Operator.IS_NONE_OF]: stringifyStandardFilterArrayValue,
  [Operator.GTE]: stringifyStandardFilterScalarValue,
  [Operator.LTE]: stringifyStandardFilterScalarValue,
  [Operator.BETWEEN]: stringifyStandardFilterRangeValue,
  [Operator.IS_EMPTY]: stringifyStandardFilterValuelessValue,
  [Operator.IS_NOT_EMPTY]: stringifyStandardFilterValuelessValue,
  [Operator.IS_TRUE]: stringifyStandardFilterValuelessValue,
  [Operator.IS_FALSE]: stringifyStandardFilterValuelessValue,
  [Operator.CURRENT_USER]: stringifyStandardFilterValuelessValue,
  [Operator.INCLUDES_TEXT]: stringifyStandardFilterArrayValue,
  [Operator.EXCLUDES_TEXT]: stringifyStandardFilterArrayValue,
  [Operator.STARTSWITH_TEXT]: stringifyStandardFilterArrayValue,
  [Operator.HAS_ONE_OF]: stringifyStandardFilterArrayValue,
  [Operator.HAS_NONE_OF]: stringifyStandardFilterArrayValue,
  [Operator.HAS_ALL_OF]: stringifyStandardFilterArrayValue,
  [Operator.HAS_ONLY]: stringifyStandardFilterArrayValue,
  [Operator.RELATIVE_RANGE]: stringifyStandardFilterRelativeRangeValue,
};

export function stringifyStandardFilterValue(operator: Operator, value: StandardFilterValue): string {
  if (!STANDARD_FILTER_OPERATOR_STRINGIFIER.hasOwnProperty(operator)) {
    throw new Error(`${operator} not found in STANDARD_FILTER_OPERATOR_STRINGIFIER`);
  }
  return STANDARD_FILTER_OPERATOR_STRINGIFIER[operator]!(value);
}

export function stringifyFilters(filters: Array<Filter>): string {
  // oxlint-disable-next-line array-callback-return
  const filterStrings = filters.map((filter): string | null => {
    const {prefix} = FILTERS[filter.key];
    if (!prefix) {
      throw new Error('Filter needs a prefix in order to be stringified');
    }
    if (isCustomTagFilter(filter)) {
      return (
        prefix +
        FilterUtil.encodeValue(filter.operator || '') +
        FILTER_PART_SEPARATOR +
        FilterUtil.encodeValue(filter.name || '') +
        FILTER_PART_SEPARATOR +
        FilterUtil.encodeValue(filter.value || '')
      );
    } else if (isFullTextFilterKey(filter.key)) {
      return `${prefix}${filter.operator}${FILTER_PART_SEPARATOR}${filter.value}`;
    } else if (isMonomerFilter(filter)) {
      return (
        prefix +
        FilterUtil.encodeValue(filter.operator) +
        FILTER_PART_SEPARATOR +
        FilterUtil.encodeValue(filter.value) +
        FILTER_PART_SEPARATOR +
        FilterUtil.encodeValue(filter.qualifier) +
        FILTER_PART_SEPARATOR +
        (Array.isArray(filter.qualifierValue)
          ? filter.qualifierValue.map((v: number) => FilterUtil.encodeValue(v)).join(FILTER_VALUE_SEPARATOR)
          : FilterUtil.encodeValue(filter.qualifierValue))
      );
    } else if (isChemicalStructureFilter(filter)) {
      return (
        prefix +
        filter.operator +
        FILTER_PART_SEPARATOR +
        // The filter value can be a molfile string, which may be thousands of characters long or
        // more, so apply URL-safe compression to avoid the value getting too big.
        compressToURLSafeBase64(filter.value) +
        FILTER_PART_SEPARATOR +
        filter.format +
        (filter.minSimilarity ? FILTER_PART_SEPARATOR + filter.minSimilarity : '') +
        (filter.reactionComponentRole != null
          ? FILTER_PART_SEPARATOR + FilterUtil.encodeValue(filter.reactionComponentRole)
          : '')
      );
    } else if (isBLASTFilter(filter)) {
      let serializedBLASTFilter =
        prefix +
        filter.operator +
        FILTER_PART_SEPARATOR +
        // BLAST queries can be long sequences, which maybe thousands of characters long or more, so apply
        // URL-safe compression to avoid the value getting too big.
        compressToURLSafeBase64(
          filter.value.textSequences
            .map((ts) => ts.name + FILTER_VALUE_SEPARATOR + ts.sequence)
            .join(FILTER_VALUE_SEPARATOR)
        ) +
        FILTER_PART_SEPARATOR +
        filter.value.apiIdentifiers.join(FILTER_VALUE_SEPARATOR);
      if (filter.params && Object.entries(filter.params).length > 0) {
        let fieldSeparator = FILTER_PART_SEPARATOR;
        // Params can be expanded over time, so old URLs might not have every parameter available, and some
        // can be unspecified. Therefore serialize key/value pairs to allow for arbitrary subsets of params
        // to be supported and ensure backwards compatibility.
        for (const [key, value] of Object.entries(filter.params)) {
          if (value) {
            serializedBLASTFilter += fieldSeparator + key + FILTER_VALUE_SEPARATOR + value;
            fieldSeparator = FILTER_VALUE_SEPARATOR;
          }
        }
      }
      return serializedBLASTFilter;
    } else if (isSingleValueFilter(filter)) {
      // eslint-disable-next-line benchlint/no-eqeqeq-null
      const parsedValue = filter.value === null ? NULL_STRING : filter.value;
      return `${prefix}${parsedValue}`;
    } else if (isMultiValueFilter(filter)) {
      return prefix + filter.operator + FILTER_PART_SEPARATOR + filter.value.join(FILTER_VALUE_SEPARATOR);
    } else if (isDatetimeFilter(filter)) {
      return prefix + filter.value.fromDatetime + FILTER_VALUE_SEPARATOR + filter.value.toDatetime;
    } else if (isFieldFilter(filter)) {
      return (
        prefix +
        FilterUtil.encodeValue(filter.operator) +
        (VALUELESS_OPERATORS.includes(filter.operator)
          ? ''
          : FILTER_PART_SEPARATOR + stringifyStandardFilterValue(filter.operator, filter.value)) +
        FILTER_PART_SEPARATOR +
        FilterUtil.encodeValue(filter.fieldId) +
        FILTER_PART_SEPARATOR +
        FilterUtil.encodeValue(filter.fieldType)
      );
    } else if (filter.key === FilterKey.REVIEW) {
      return prefix + filter.value.review_status + ReviewConstants.ROLE_FILTER_SEPARATOR + filter.value.role;
    } else if (
      filter.key === FilterKey.CONTAINER_QUANTITY ||
      filter.key === FilterKey.CONTAINER_CONCENTRATION
    ) {
      return (
        prefix +
        filter.operator +
        FILTER_PART_SEPARATOR +
        filter.value.value +
        FILTER_PART_SEPARATOR +
        filter.value.units
      );
    } else if (filter.key === FilterKey.GRID_EMPTY_POSITIONS) {
      return prefix + filter.operator + FILTER_PART_SEPARATOR + filter.value;
    } else if (filter.key === FilterKey.CONTAINER_CONTENTS) {
      return (
        prefix +
        filter.operator +
        FILTER_PART_SEPARATOR +
        filter.value.contentIds.join(FILTER_VALUE_SEPARATOR)
      );
    } else if (filter.key === FilterKey.NOTE_REFERENCE) {
      return (
        prefix +
        filter.operator +
        FILTER_PART_SEPARATOR +
        filter.value.referencedIds.join(FILTER_VALUE_SEPARATOR) +
        FILTER_PART_SEPARATOR +
        filter.value.referencedType
      );
    } else if (filter.key === FilterKey.ALPHAFOLD_STRUCTURE) {
      return prefix + filter.operator;
    } else if (isStandardFilter(filter)) {
      const stringifiedWithoutValue = prefix + FilterUtil.encodeValue(filter.operator);
      const result = VALUELESS_OPERATORS.includes(filter.operator)
        ? stringifiedWithoutValue
        : stringifiedWithoutValue +
          FILTER_PART_SEPARATOR +
          stringifyStandardFilterValue(filter.operator, filter.value);
      return result;
    } else {
      throw new Error(`Unsupported filter ${filter}`);
    }
  });
  return filterStrings.filter((filterString) => filterString != null).join(FILTER_SEPARATOR);
}

export function parseSortsString(sortsString: string, urlVersion: number): Array<Sort> {
  if (!sortsString) {
    return [];
  }

  const parseFunction = urlVersion < 1 ? parseUnencodedSortString : parseEncodedSortString;
  const parsedSorts = sortsString
    .split(SORT_SEPARATOR)
    .map((sortString): Sort | null => parseFunction(sortString));
  return _.compact(parsedSorts);
}

export function parseUnencodedSortString(sortString: string): Sort | null {
  const sortParsedByCurrentSeparator = parseSortKeyAndReverse(sortString, SORT_PART_SEPARATOR);
  if (sortParsedByCurrentSeparator) {
    return sortParsedByCurrentSeparator;
  }
  const sortParsedByLegacySeparator = parseSortKeyAndReverse(sortString, ',');
  if (sortParsedByLegacySeparator) {
    return sortParsedByLegacySeparator;
  }
  assert(false, `Malformed sort string ${sortString}`);
  return null;
}

function parseSortKeyAndReverse(sortString: string, separator: string): Sort | null {
  const rawValueParts = sortString.split(separator);
  const parsedKey = rawValueParts.slice(0, rawValueParts.length - 1).join(separator);
  const parsedReverse = rawValueParts[rawValueParts.length - 1];
  if (![SORT_REVERSE_FALSE_URL_STRING, SORT_REVERSE_TRUE_URL_STRING].includes(parsedReverse)) {
    return null;
  }
  return {
    key: parsedKey,
    reverse: parsedReverse === SORT_REVERSE_TRUE_URL_STRING,
  };
}

export function parseEncodedSortString(sortString: string): Sort | null {
  const rawValueParts = sortString.split(SORT_PART_SEPARATOR);
  if (rawValueParts.length !== 2) {
    assert(false, `Malformed sort string ${sortString}`);
    return null;
  }
  return {
    key: FilterUtil.decodeValue(rawValueParts[0]),
    reverse: rawValueParts[1] === SORT_REVERSE_TRUE_URL_STRING,
  };
}

export function stringifySorts(sorts: Array<Sort>): string {
  const sortsStrings = sorts.map(
    (sort): string =>
      FilterUtil.encodeValue(sort.key) +
      SORT_PART_SEPARATOR +
      (sort.reverse ? SORT_REVERSE_TRUE_URL_STRING : SORT_REVERSE_FALSE_URL_STRING)
  );
  return sortsStrings.join(SORT_SEPARATOR);
}

function getVersionFromQueryParam(v: string | undefined): number {
  if (!v) {
    return PRE_VERSIONING_DEFAULT;
  }
  const parsedVersion = parseInt(decodeJQueryParam(v), 10);
  return Number.isInteger(parsedVersion) ? parsedVersion : PRE_VERSIONING_DEFAULT;
}

export interface QueryKey {
  filter?: string;
  limit?: string;
  offset?: string;
  openProjectSettings?: boolean;
  openManageAccess?: boolean;
  sorts?: string;
  q?: string;
  groupBy?: string;
  v?: string;
  // Preserved for backwards compatibility
  reverse?: string;
  sort?: string;
}

export function searchParamsFromQueryKey(queryKey: QueryKey): Partial<SearchParams> {
  const {filter, limit, offset, sort, reverse, sorts, groupBy, v, q} = queryKey;
  const params: Partial<SearchParams> = {};
  const urlVersion = getVersionFromQueryParam(v);
  if (q != null) {
    params.query = decodeJQueryParam(q);
  }
  if (filter) {
    params.filters = sanitizeEmptyFilters(parseFiltersString(decodeJQueryParam(filter), urlVersion));
  }
  if (limit) {
    params.limit = Number(limit);
  }
  if (offset) {
    params.offset = Number(offset);
  }
  const sortsFromQueryKey = getSortsFromQueryKey(urlVersion, sort, reverse, sorts);
  if (sortsFromQueryKey.length > 0) {
    params.sorts = sortsFromQueryKey;
  }

  if (groupBy && Object.values(GroupByKey).includes(groupBy as GroupByKey)) {
    if (params.filters) {
      const blastFilter = FilterUtil.getFilterByKey(params.filters, FilterKey.BLAST) as BLASTFilter;

      if (blastFilter && isMultiQueryBLAST(blastFilter)) {
        params.groupBy = {
          childLimit: getSelectedPageSize(),
          childOffset: 0,
          groupField: groupBy,
        };
      } else {
        params.groupBy = {
          groupField: groupBy,
          childLimit: 0,
          childOffset: 0,
        };
      }
    } else {
      params.groupBy = {
        groupField: groupBy,
        childLimit: 0,
        childOffset: 0,
      };
    }
  }
  return params;
}

function getSortsFromQueryKey(
  urlVersion: number,
  sort?: string,
  reverse?: string,
  sorts?: string
): Array<Sort> {
  if (sort) {
    assert(!sorts, 'Only one of sort or sorts should be defined');
    return [{key: sort, reverse: reverse === SORT_REVERSE_TRUE_URL_STRING}];
  }
  if (sorts) {
    return parseSortsString(decodeJQueryParam(sorts), urlVersion);
  }

  return [];
}

/**
 * Get the new URL based on search params.
 */
export function getNavigationURL(
  baseURL: string,
  searchParams: SearchParams,
  filterErrors: FilterErrors,
  version: number = URL_VERSION
): string {
  const {filters, offset, limit, sorts, query, groupBy} = sanitizeSearchParams(searchParams, filterErrors);
  const values = {
    filter: stringifyFilters(filters),
    offset,
    limit,
    sorts: stringifySorts(sorts),
    q: query,
    groupBy: groupBy?.groupField,
    v: version,
  };
  return setURLParams(baseURL, values);
}

interface FolderListingParams {
  folder: StringIDFolder;
  isExpanded: boolean;
  searchParams: SearchParams;
  filterErrors: FilterErrors;
}

export function getFolderListingURL(
  prevParams: FolderListingParams,
  params: FolderListingParams
): NavigationUpdate | null {
  if (!params.isExpanded) {
    return null;
  }
  return {
    url: getNavigationURL(params.folder.url, params.searchParams, params.filterErrors),
    options: {
      replace:
        prevParams.searchParams != null &&
        prevParams.folder.url === params.folder.url &&
        prevParams.isExpanded === params.isExpanded &&
        Boolean(prevParams.searchParams.query) === Boolean(params.searchParams.query),
    },
  };
}
